import { ref } from 'vue';
import { downloadBlobFile } from '../utils/downloadBlobFile';
import { handleApiError } from '../utils/errors';
import { useCustomToast } from './useCustomToast';

export type ExcelBlobExportFn = () => Promise<{
  blob: Blob;
  exportedCount: number;
  filename: string;
}>;

export interface ExcelBlobExportLabel {
  singular: string;
  plural: string;
  gender?: 'male' | 'female';
}

export function useExcelBlobExport(exportFn: ExcelBlobExportFn, entityLabel: ExcelBlobExportLabel) {
  const toast = useCustomToast();
  const isExporting = ref(false);

  const handleExportToExcel = async () => {
    if (isExporting.value) {
      return;
    }

    isExporting.value = true;

    const progressToastId = toast.showWarningToast(
      {
        title: 'Exportação em andamento',
        message: 'Por favor, aguarde.',
        type: 'warning',
      },
      { timeout: false }
    );

    try {
      const { blob, exportedCount, filename } = await exportFn();

      downloadBlobFile(blob, filename);

      const exported = entityLabel.gender === 'female' ? 'exportada' : 'exportado';
      const successMessage =
        exportedCount === 1
          ? `1 ${entityLabel.singular} ${exported}`
          : `${exportedCount} ${entityLabel.plural} ${exported}s`;

      toast.showSuccessToast({
        title: 'Exportação concluída com sucesso',
        message: successMessage,
        type: 'success',
      });
    } catch (error) {
      const friendlyError = handleApiError(error, 'Tente novamente.');
      toast.showErrorToast({
        title: 'Erro ao exportar',
        message: friendlyError.message,
        type: 'error',
      });
    } finally {
      toast.dismissToast(progressToastId);
      isExporting.value = false;
    }
  };

  return {
    handleExportToExcel,
    isExporting,
  };
}
